import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { formatDate, profileTypeLabel } from '../../lib/utils';
import { apiGet } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/WorkflowTracker';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Search } from 'lucide-react';

const ALL_STATUSES = ['all', 'lead', 'documents_submitted', 'under_review', 'offer_letter', 'visa_processing', 'enrolled', 'rejected'];

export default function AllCandidates({ isPartner = false }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  // Follow the URL, e.g. clicking the Candidates menu link after opening /candidates?status=enrolled.
  useEffect(() => { setStatusFilter(searchParams.get('status') || 'all'); }, [searchParams]);

  const basePath = isPartner ? '/partner' : '/admin';

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Wait for typing to pause before searching.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // A newer search cancels the older request, so a slow stale reply can't overwrite the list.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    apiGet(`/candidates?${params.toString()}`, { signal: controller.signal })
      .then(r => setCandidates(r.data))
      .catch(err => { if (err.code !== 'ERR_CANCELED') setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [debouncedSearch, statusFilter, attempt]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="all-candidates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>
            {isPartner ? 'My Candidates' : 'All Candidates'}
          </h1>
          <p className="text-ink-2 mt-1">{isPartner ? 'Manage your student applications' : 'View all partner submissions'}</p>
        </div>
        {isPartner && (
          <Link to="/partner/candidates/new" data-testid="add-candidate-btn" className="inline-flex items-center gap-2 bg-aqua hover:bg-aqua/90 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors">
            + Add Candidate
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone..." data-testid="candidate-search-input" className="pl-10 h-11 border-contrast/10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 h-11" data-testid="status-filter" aria-label="Filter by stage">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {ALL_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All Statuses' : s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>
      ) : loadError ? (
        <LoadError what="the candidate list" onRetry={() => setAttempt(a => a + 1)} />
      ) : candidates.length === 0 ? (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card p-12 text-center">
          <Users className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2">No candidates found</p>
        </div>
      ) : (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="candidates-table">
              <thead>
                <tr className="bg-contrast/5 border-b border-contrast/10">
                  <th className="text-left py-3 px-4 font-medium text-ink-2">XpertID</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Profile</th>
                  {!isPartner && <th className="text-left py-3 px-4 font-medium text-ink-2">Partner</th>}
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Country</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Course</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.id} className="border-b border-contrast/[0.07] hover:bg-contrast/5" data-testid={`candidate-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <span className="whitespace-nowrap text-xs font-mono font-semibold text-brand bg-surface-3 px-2 py-0.5 rounded">{c.xpert_id || 'N/A'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <Link to={`${basePath}/candidates/${c.id}`} className="text-ink hover:text-brand font-medium">
                        {c.name || `${c.first_name || ''} ${c.last_name || ''}`}
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-full font-medium ${c.profile_type === 'phd' ? 'bg-brand/10 text-iris' : (c.profile_type || 'masters') === 'masters' ? 'bg-surface-3 text-brand' : 'bg-surface-3 text-aqua'}`}>
                        {profileTypeLabel(c.profile_type)}
                      </span>
                    </td>
                    {!isPartner && <td className="py-3 px-4 text-ink-2 min-w-[11rem]">{c.partner_name || 'N/A'}{c.partner_organization && <span className="block text-xs text-ink-3">{c.partner_organization}</span>}</td>}
                    <td className="py-3 px-4 text-ink-2">{c.country || c.target_country || 'N/A'}</td>
                    <td className="py-3 px-4 text-ink-2 min-w-[14rem]">{c.course_interested || c.target_course || 'N/A'}</td>
                    <td className="py-3 px-4"><StatusBadge status={c.status} /></td>
                    <td className="py-3 px-4 text-ink-3 text-xs whitespace-nowrap">{c.created_at ? formatDate(c.created_at) : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
